import axios, { isAxiosError } from "../../axios";
import { AppDispatch, RootState } from "../../redux-store";
import { meT } from "dora-contracts";
import * as tPromise from "io-ts-promise";
import { Me } from "./types";
import {
  logoutChannel,
  sessionChannel,
  SessionAnnouncement,
} from "../../helpers/broadcast-channel";
import { createErrorReportingAsyncThunk } from "../helpers";
import { reportWarning } from "../../helpers/error-helper";
import {
  readSavedIds,
  readSavedTeamIds,
  readSavedTeamIdsByType,
  setStorageUser,
} from "../../helpers/user-storage";
import { CARGOS_REPORT_TEAMS_FILTER } from "../data/cargos-report";
import {
  DISPATCHING_FILTER_TYPES,
  DISPATCHING_VIEW_PAGE_CARGO_TAG_FILTER,
  DISPATCHING_VIEW_PAGE_TEAMS_FILTER,
} from "../app/dispatching";
import { LIVE_VIEW_FILTERED_TEAMS_FILTER } from "../data/live-view";
import {
  CALENDAR_VIEW_FILTER_TYPES,
  CALENDAR_VIEW_PAGE_TEAM_FILTER,
} from "../data/calendar-view";
import { UPCOMING_PAGE_TEAMS_FILTER } from "../app/upcoming-page";
import { FLEET_MANAGEMENT_TEAMS_FILTER } from "../app/fleet-management";
import { DRIVERS_MANAGEMENT_TEAMS_FILTER } from "../data/drivers";
import * as upcomingActions from "../app/upcoming-page";
import * as dispatchingActions from "../app/dispatching";
import * as calendarViewActions from "../data/calendar-view";
import * as liveViewActions from "../data/live-view";
import * as cargoReportActions from "../data/cargos-report";
import * as fleetManagementActions from "../app/fleet-management";
import * as driversManagementActions from "../data/drivers";

export const AUTH_STATUS_UNINITIALIZED = "UNINITIALIZED";
export const AUTH_STATUS_AUTHENTICATING = "AUTHENTICATING";
export const AUTH_STATUS_AUTHENTICATED = "AUTHENTICATED";
export const AUTH_STATUS_UNAUTHENTICATED = "UNAUTHENTICATED";
export const AUTH_STATUS_ERROR = "AUTH_ERROR";

type AuthStatus =
  | typeof AUTH_STATUS_ERROR
  | typeof AUTH_STATUS_UNINITIALIZED
  | typeof AUTH_STATUS_AUTHENTICATING
  | typeof AUTH_STATUS_AUTHENTICATED
  | typeof AUTH_STATUS_UNAUTHENTICATED;

export type User = Me;

interface State {
  status: AuthStatus;
  user: null | User;
  // The first user this tab loaded: what everything it has on screen belongs
  // to, even after a re-check finds the shared session moved to someone else.
  loadedUserId: string | null;
  // Who the shared session belongs to now, as last seen by this tab's own
  // auth check or announced by another tab.
  sessionUserId: string | null;
}

const initialState: State = {
  status: AUTH_STATUS_UNINITIALIZED,
  user: null,
  loadedUserId: null,
  sessionUserId: null,
};

const reducer = (state: State = initialState, action: any): State => {
  switch (action.type) {
    case "auth/check/begun":
      if (state.status === AUTH_STATUS_ERROR) {
        return state;
      }
      return {
        ...state,
        status: AUTH_STATUS_AUTHENTICATING,
        user: null,
      };
    case "auth/check/completed": {
      const { user } = action.payload;
      return {
        status: user ? AUTH_STATUS_AUTHENTICATED : AUTH_STATUS_UNAUTHENTICATED,
        user,
        loadedUserId: state.loadedUserId ?? user?.id ?? null,
        sessionUserId: user ? user.id : state.sessionUserId,
      };
    }
    case "auth/check/failed": {
      return {
        ...state,
        status: AUTH_STATUS_ERROR,
        user: null,
      };
    }
    case "auth/loggedOut": {
      return {
        ...initialState,
        status: AUTH_STATUS_UNAUTHENTICATED,
      };
    }
    case "auth/sessionAnnounced": {
      return {
        ...state,
        sessionUserId: action.payload.userId,
      };
    }
    default:
      return state;
  }
};

const decodeMe = tPromise.decode(meT);

export const checkAuthState =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch({ type: "auth/check/begun" });
    try {
      const response = await axios.get("/auth/me");
      const user = await decodeMe(response.data);

      const userFromState = getState().auth.user;
      if (!userFromState) {
        // not initialised, so this is first "sync"

        const teamIds = user.teamIds;

        // Filters are saved per user, so they can only be read now that the
        // user is known. Saved team ids the user no longer has are dropped;
        // a filter with nothing usable left starts out with all teams.
        setStorageUser(user.id);

        dispatch(
          upcomingActions.setAllFilterTeamIds(
            readSavedTeamIds(UPCOMING_PAGE_TEAMS_FILTER, teamIds) ?? teamIds
          )
        );
        const savedDispatchFilters = readSavedTeamIdsByType(
          DISPATCHING_VIEW_PAGE_TEAMS_FILTER,
          teamIds,
          DISPATCHING_FILTER_TYPES
        );
        dispatch(
          savedDispatchFilters
            ? dispatchingActions.setTeamFilterOptions(savedDispatchFilters)
            : dispatchingActions.setAllFilterOptions(teamIds)
        );
        // Cargo tags load later, so they can't be checked here; a tag that
        // no longer exists simply matches nothing.
        dispatch(
          dispatchingActions.setCargoTagFilterIds(
            readSavedIds(DISPATCHING_VIEW_PAGE_CARGO_TAG_FILTER) ?? []
          )
        );
        const savedCalendarFilters = readSavedTeamIdsByType(
          CALENDAR_VIEW_PAGE_TEAM_FILTER,
          teamIds,
          CALENDAR_VIEW_FILTER_TYPES
        );
        dispatch(
          savedCalendarFilters
            ? calendarViewActions.setTeamFilterOptions(savedCalendarFilters)
            : calendarViewActions.setAllFilterOptions(teamIds)
        );
        dispatch(
          liveViewActions.setSelectedTeams(
            readSavedTeamIds(LIVE_VIEW_FILTERED_TEAMS_FILTER, teamIds) ??
              teamIds
          )
        );
        dispatch(
          cargoReportActions.setSelectedTeams(
            readSavedTeamIds(CARGOS_REPORT_TEAMS_FILTER, teamIds) ?? teamIds
          )
        );
        dispatch(
          fleetManagementActions.setSelectedTeams(
            readSavedTeamIds(FLEET_MANAGEMENT_TEAMS_FILTER, teamIds) ?? teamIds
          )
        );
        dispatch(
          driversManagementActions.setSelectedTeams(
            readSavedTeamIds(DRIVERS_MANAGEMENT_TEAMS_FILTER, teamIds) ??
              teamIds
          )
        );
      }

      dispatch({ type: "auth/check/completed", payload: { user } });
      sessionChannel.postMessage({
        type: "session",
        userId: user.id,
      } satisfies SessionAnnouncement);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 401) {
        return dispatch({
          type: "auth/check/completed",
          payload: { user: null },
        });
      }
      return dispatch({ type: "auth/check/failed" });
    }
  };

// A dropped connection (e.g. "Log out" clicked in a tab that just woke from
// sleep) gets the network-error toast and leaves the user logged in, so they
// can retry, instead of escaping as an unhandled rejection (DISPATCHER-X).
// Another tab loaded the shared session as `userId`.
export const sessionAnnounced = (userId: string) => ({
  type: "auth/sessionAnnounced" as const,
  payload: { userId },
});

export type SessionRecheckResult = "LOGGED_OUT" | "RECONNECT" | "RETRY_LATER";

// The socket asks for this when the server refuses its handshake as
// unauthorized while this tab still thinks it's logged in. It runs in the
// background, so the expected outcomes return a result instead of throwing,
// which would show an error toast for something the user didn't do:
//   * 401: the session is gone (e.g. expired), so log this tab out.
//   * 200: the session is valid again (e.g. logged back in from another tab),
//     so the socket can reconnect.
//   * no response, 429 or 5xx: nothing is known yet, try again later.
// Unlike checkAuthState this doesn't pass through AUTHENTICATING, which would
// unmount the page while the check runs. Only a malformed /auth/me response
// throws.
export const recheckSession = createErrorReportingAsyncThunk(
  "auth/recheckSession",
  async (_: void, { dispatch }): Promise<SessionRecheckResult> => {
    let response;
    try {
      response = await axios.get("/auth/me");
    } catch (e) {
      if (!isAxiosError(e)) throw e;
      const status = e.response?.status;
      if (status === 401) {
        dispatch({ type: "auth/loggedOut" });
        setStorageUser(null);
        return "LOGGED_OUT";
      }
      if (status === undefined || status === 429 || status >= 500) {
        if (status !== undefined) {
          reportWarning({
            type: "Session recheck failed",
            status,
          });
        }
        return "RETRY_LATER";
      }
      throw e;
    }
    await decodeMe(response.data);
    return "RECONNECT";
  }
);

export const logout = createErrorReportingAsyncThunk(
  "auth/logout",
  async (_: void, { dispatch }) => {
    try {
      await axios.post("/auth/logout");
    } catch (e) {
      // The session already expired server-side: finish logging out here.
      if (!(isAxiosError(e) && e.response?.status === 401)) throw e;
    }
    dispatch({ type: "auth/loggedOut" });
    setStorageUser(null);
    logoutChannel.postMessage("logout");
  }
);

export default reducer;
